/**
 * Hook to calculate ionoff response costs using OR sub-key delta
 * 
 * Primary method: (post-prompt OR usage) - (pre-prompt OR usage) - (sum of individual AI costs) - (coaching cost)
 * Fallback method: Sum of all header-based costs for the prompt - (sum of individual AI costs) - (coaching cost)
 * 
 * Fallback is used when delta is unreliable:
 * - User clicked stop before EOP
 * - Parallel prompts were sent (multiple prompts in flight)
 * - User edited a prompt
 * 
 * Note: Coaching costs are subtracted because they're charged to the user's sub-key
 * but are not part of the ionoff synthesis/grading process.
 */

import { useCallback, useRef, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase/config';
import { Message } from '../types';

// Track which prompts need fallback cost calculation
interface PromptCostState {
  promptId: string;
  useFallback: boolean;
  prePromptOrUsage: number | null;
  coachingCost: number | null; // Track coaching cost to subtract from delta
}

export const useIonoffCost = (conversationId: string | null) => {
  // Track prompts that need fallback cost calculation
  const promptCostStates = useRef<Map<string, PromptCostState>>(new Map());
  
  // Track active (in-flight) prompts for parallel detection
  const activePromptIds = useRef<Set<string>>(new Set());

  /**
   * Called when user sends a new prompt
   * Detects parallel prompting and marks both prompts for fallback if needed
   */
  const onPromptSent = useCallback((promptId: string, prePromptOrUsage?: number) => {
    // If there are already active prompts, mark ALL for fallback (including this one)
    const hasParallel = activePromptIds.current.size > 0;
    
    if (hasParallel) {
      console.log('⚠️ Parallel prompting detected - using fallback cost for all active prompts');
      // Mark existing active prompts for fallback
      Array.from(activePromptIds.current).forEach(existingId => {
        const existing = promptCostStates.current.get(existingId);
        if (existing) {
          existing.useFallback = true;
        }
      });
    }
    
    // Add this prompt
    activePromptIds.current.add(promptId);
    promptCostStates.current.set(promptId, {
      promptId,
      useFallback: hasParallel, // Mark for fallback if parallel
      prePromptOrUsage: prePromptOrUsage ?? null,
      coachingCost: null, // Will be set when coaching response arrives
    });
    
    console.log('💰 Prompt cost tracking started', {
      promptId,
      useFallback: hasParallel,
      prePromptOrUsage,
      activePrompts: activePromptIds.current.size,
    });
  }, []);

  /**
   * Called when coaching response arrives with its cost
   * Stores the cost for subtraction during ionoff cost calculation
   */
  const onCoachingCostReceived = useCallback((promptId: string, coachingCost: number) => {
    const state = promptCostStates.current.get(promptId);
    if (state) {
      state.coachingCost = coachingCost;
      console.log('💰 Coaching cost received for prompt', { promptId, coachingCost });
    }
  }, []);

  /**
   * Called when user clicks stop on a prompt
   * Marks that prompt for fallback cost calculation
   */
  const onPromptStopped = useCallback((promptId: string) => {
    const state = promptCostStates.current.get(promptId);
    if (state) {
      state.useFallback = true;
      console.log('⚠️ Prompt stopped - using fallback cost', { promptId });
    }
  }, []);

  /**
   * Calculate ionoff response cost after it arrives
   * Returns the calculated cost or null if calculation fails
   * 
   * Formula (delta): totalPromptCost - sumOfAiCosts - coachingCost
   * Formula (fallback): totalFromHeaders - sumOfAiCosts - coachingCost
   * 
   * We subtract coaching cost because it's charged to the user's sub-key
   * but is NOT part of the ionoff synthesis/grading process.
   */
  const calculateIonoffCost = useCallback(async (
    ionoffMessage: Message,
    allMessages: Message[],
  ): Promise<{ cost: number; method: 'delta' | 'fallback' } | null> => {
    if (!conversationId || !ionoffMessage.userPromptMessageId) {
      console.warn('Cannot calculate ionoff cost - missing conversation or prompt ID');
      return null;
    }

    const promptId = ionoffMessage.userPromptMessageId;
    const state = promptCostStates.current.get(promptId);
    
    // Find the user prompt message to get prePromptOrUsage
    const userPrompt = allMessages.find(m => m.id === promptId && m.role === 'user');
    const prePromptOrUsage = userPrompt?.prePromptOrUsage ?? state?.prePromptOrUsage;
    const useFallback = state?.useFallback || userPrompt?.useFallbackCost || !prePromptOrUsage;
    
    // Get coaching cost (if coaching ran for this prompt)
    const coachingCost = state?.coachingCost || 0;

    // Get individual AI response costs for this prompt
    const aiResponses = allMessages.filter(m => 
      m.role === 'assistant' &&
      m.userPromptMessageId === promptId &&
      m.isPromptResponse === true &&
      !m.isSynthesized &&
      !m.isGradingResponse &&
      !m.isDebugInfo &&
      !m.isCostSummary &&
      !m.isGradeMatrix
    );
    
    const sumOfAiCosts = aiResponses.reduce((sum, m) => sum + (m.costUSD || 0), 0);
    console.log('💰 Cost components for ionoff calculation', { 
      promptId, 
      numResponses: aiResponses.length,
      sumOfAiCosts,
      coachingCost,
    });

    if (useFallback) {
      // FALLBACK: Sum all header-based costs for this prompt
      console.log('💰 Using fallback cost calculation (sum of headers)');
      
      // Get ALL assistant messages for this prompt (including grading, synthesis API calls, etc.)
      const allAssistantMessages = allMessages.filter(m =>
        m.role === 'assistant' &&
        m.userPromptMessageId === promptId &&
        m.costUSD !== undefined
      );
      
      const totalFromHeaders = allAssistantMessages.reduce((sum, m) => sum + (m.costUSD || 0), 0);
      
      // ionoff cost = total - individual AI costs - coaching cost (all displayed separately)
      const ionoffCost = Math.max(0, totalFromHeaders - sumOfAiCosts - coachingCost);
      
      console.log('💰 Fallback ionoff cost calculated', {
        promptId,
        totalFromHeaders,
        sumOfAiCosts,
        coachingCost,
        ionoffCost,
      });
      
      return { cost: ionoffCost, method: 'fallback' };
    }

    // DELTA: Fetch current OR usage and calculate delta
    console.log('💰 Using delta cost calculation (OR sub-key)');
    
    try {
      const functions = getFunctions();
      const getCurrentOrUsage = httpsCallable(functions, 'getCurrentOrUsage');
      const result = await getCurrentOrUsage();
      const data = result.data as { success: boolean; usage: number | null };
      
      if (!data.success || data.usage === null) {
        console.warn('Failed to get current OR usage, falling back to header sum');
        // Fall through to fallback
        const allAssistantMessages = allMessages.filter(m =>
          m.role === 'assistant' &&
          m.userPromptMessageId === promptId &&
          m.costUSD !== undefined
        );
        const totalFromHeaders = allAssistantMessages.reduce((sum, m) => sum + (m.costUSD || 0), 0);
        const ionoffCost = Math.max(0, totalFromHeaders - sumOfAiCosts - coachingCost);
        return { cost: ionoffCost, method: 'fallback' };
      }
      
      const postPromptOrUsage = data.usage;
      const totalPromptCost = postPromptOrUsage - (prePromptOrUsage || 0);
      
      // ionoff cost = total prompt cost - individual AI costs - coaching cost
      const ionoffCost = Math.max(0, totalPromptCost - sumOfAiCosts - coachingCost);
      
      console.log('💰 Delta ionoff cost calculated', {
        promptId,
        prePromptOrUsage,
        postPromptOrUsage,
        totalPromptCost,
        sumOfAiCosts,
        coachingCost,
        ionoffCost,
      });
      
      return { cost: ionoffCost, method: 'delta' };
    } catch (error) {
      console.error('Failed to calculate delta cost, using fallback', error);
      
      // Fallback on error
      const allAssistantMessages = allMessages.filter(m =>
        m.role === 'assistant' &&
        m.userPromptMessageId === promptId &&
        m.costUSD !== undefined
      );
      const totalFromHeaders = allAssistantMessages.reduce((sum, m) => sum + (m.costUSD || 0), 0);
      const ionoffCost = Math.max(0, totalFromHeaders - sumOfAiCosts - coachingCost);
      return { cost: ionoffCost, method: 'fallback' };
    }
  }, [conversationId]);

  /**
   * Persist the calculated ionoff cost to Firestore
   */
  const persistIonoffCost = useCallback(async (
    ionoffMessageId: string,
    cost: number,
    method: 'delta' | 'fallback',
  ) => {
    if (!conversationId) return;
    
    try {
      const messageRef = doc(db, 'conversations', conversationId, 'messages', ionoffMessageId);
      await updateDoc(messageRef, {
        costUSD: cost,
        costMethod: method,
        costCalculated: true,
      });
      
      console.log('💰 Ionoff cost persisted', {
        ionoffMessageId,
        cost,
        method,
      });
    } catch (error) {
      console.error('Failed to persist ionoff cost', error);
    }
  }, [conversationId]);

  /**
   * Called when a prompt reaches EOP (ionoff response arrived)
   * Removes from active tracking
   */
  const onPromptComplete = useCallback((promptId: string) => {
    activePromptIds.current.delete(promptId);
    console.log('💰 Prompt completed, removed from active tracking', {
      promptId,
      remainingActive: activePromptIds.current.size,
    });
  }, []);

  // Reset state when conversation changes
  useEffect(() => {
    promptCostStates.current.clear();
    activePromptIds.current.clear();
  }, [conversationId]);

  return {
    onPromptSent,
    onPromptStopped,
    onPromptComplete,
    onCoachingCostReceived,
    calculateIonoffCost,
    persistIonoffCost,
  };
};
